import { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { EMAIL_CONFIRM_REDIRECT, RESET_PASSWORD_REDIRECT } from '../config';
import { socialAuthAvailable, signInWithApple, signInWithGoogle, SOCIAL_CANCELLED } from '../socialAuth';

// ─── Decorative card stack (landing screen only) ─────────────────────────────
// Three fanned listing cards sit above the wordmark to pre-teach the swipe
// gesture and show what the app is. Per the design 5a handoff these should be
// the first three listings from the feed prefetch when it has resolved; at the
// unauthenticated auth screen there is no personal feed, so we ship a small
// curated static set. Photos are the same royalty-free Unsplash interiors used
// by the demo feed (hotlinked; each card falls back to a solid navy tile if the
// image fails, so the layout never breaks offline). The stack is NOT swipeable
// here — it is purely decorative.
const stackImg = (id, w = 720) =>
  `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=80`;

const STACK_LISTINGS = [
  // back-left
  { neighborhood: 'Bushwick, Brooklyn', figures: '$1,850/mo · studio', photo: stackImg('1484154218962-a197022b5858') },
  // back-right
  { neighborhood: 'Capitol Hill, Seattle', figures: '$2,300/mo · 2 beds', photo: stackImg('1493809842364-78817add7ffb') },
  // front
  { neighborhood: 'Murray Hill, Manhattan', figures: '$7,000/mo · 3 beds', photo: stackImg('1522708323590-d24dbb6b0267') },
];

// One-time entrance + press feedback + input styling. Everything animated is
// wrapped in `prefers-reduced-motion: no-preference`, and the initial hidden
// state lives there too — so reduced-motion users see the screen fully rendered
// with no fade and no press scale.
const SCREEN_CSS = `
.nomad-auth .n-press { transition: transform .12s ease, filter .12s ease; -webkit-tap-highlight-color: transparent; }
.nomad-auth .n-input { transition: border-color .15s ease, background .15s ease; }
.nomad-auth .n-input::placeholder { color: rgba(159,180,214,.65); }
.nomad-auth .n-input:focus { border-color: rgba(143,196,188,.55); background: rgba(255,255,255,.12); }
@media (prefers-reduced-motion: no-preference) {
  .nomad-auth .n-press:active { transform: scale(.94); }
  .nomad-auth .n-enter { opacity: 0; animation: nomadRise .34s ease forwards; }
  .nomad-auth .n-enter-cards { opacity: 0; animation: nomadFade .5s ease forwards; }
  .nomad-auth .n-enter-wordmark { animation-delay: .12s; }
  .nomad-auth .n-enter-buttons { animation-delay: .18s; }
}
@keyframes nomadRise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
@keyframes nomadFade { from { opacity: 0; } to { opacity: 1; } }
`;

const AppleGlyph = () => (
  <svg viewBox="0 0 24 24" width="19" height="19" fill="#fff" aria-hidden="true">
    <path d="M16.365 1.43c0 1.14-.42 2.2-1.24 3.02-.98.98-2.16 1.55-3.44 1.44-.02-1.13.44-2.24 1.24-3.04.86-.86 2.28-1.5 3.44-1.42zM20.9 17.02c-.55 1.27-.82 1.84-1.53 2.97-.99 1.57-2.39 3.53-4.12 3.54-1.54.02-1.94-1-4.03-.99-2.09.01-2.53 1.01-4.07.99-1.73-.01-3.05-1.78-4.04-3.35C-.02 16.8-.3 12.2 1.13 9.65c.99-1.79 2.63-2.93 4.43-2.93 1.77 0 2.88 1 4.35 1 1.42 0 2.29-1 4.35-1 1.55 0 3.2.85 4.36 2.31-3.83 2.1-3.2 7.55.28 8.99z" />
  </svg>
);

const GoogleGlyph = () => (
  <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(''); // non-error status (e.g. "reset link sent")
  const [showExistingModal, setShowExistingModal] = useState(false); // "you already have an account" popup
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user, profile, loading: authLoading } = useAuth();
  const [socialLoading, setSocialLoading] = useState('');
  const [emailMode, setEmailMode] = useState(false);
  const showSocial = socialAuthAvailable();
  // The website with web-social off (or any no-social case) skips the chooser and
  // shows the email form directly — there'd be nothing else to choose.
  const showChooser = showSocial && !emailMode;
  // When there are no social options at all, the email form is the primary
  // (and only) screen, so it gets the full-size wordmark instead of the compact one.
  const formIsPrimary = !showSocial;

  // Already signed in — redirect to the right home.
  // Route on the ACTUAL role: only a complete profile with a known role goes
  // straight to its home. Anything else (incomplete, null/unknown role) goes to
  // /role-select, which is the single safe place to resolve role state. This
  // avoids sending an unknown-role account into /swipe, where the guard would
  // bounce it and freeze the app.
  useEffect(() => {
    if (authLoading || !user) return;
    if (profile?.profile_complete && profile.role === 'host') {
      navigate('/host-dashboard', { replace: true });
    } else if (profile?.profile_complete && profile.role === 'subleasee') {
      navigate('/swipe', { replace: true });
    } else {
      navigate('/role-select', { replace: true });
    }
  }, [user, profile, authLoading, navigate]);

  // Sends a password-reset email to the address in the email field. Uses the
  // same email input as sign in — no separate screen. resetPasswordForEmail
  // always resolves successfully whether or not the address has an account
  // (Supabase does this on purpose to avoid leaking which emails are registered),
  // so the confirmation copy is deliberately worded "if an account exists".
  const handleForgotPassword = async () => {
    setError('');
    setNotice('');
    if (!email) {
      setError('Enter your email above, then tap "Forgot password?" for a reset link.');
      return;
    }
    setLoading(true);
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: RESET_PASSWORD_REDIRECT,
      });
      if (resetError) throw resetError;
      setNotice(`If an account exists for ${email}, a password reset link is on its way. Check your inbox (and spam).`);
    } catch (err) {
      setError(err.message || 'Could not send the reset email — try again in a moment.');
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    setError('');
    setNotice('');
    setLoading(true);

    try {
      if (isSignUp) {
        if (password !== confirmPassword) {
          setError("Passwords don't match. Please re-enter them.");
          setLoading(false);
          return;
        }
        const { data, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          // Where Supabase sends the user after they click the confirmation link.
          // Resolves to the hosted web page (nomadmktplace.com/email-confirmed),
          // which works whether signup happened on web or in the native app.
          options: { emailRedirectTo: EMAIL_CONFIRM_REDIRECT },
        });
        if (signUpError) throw signUpError;

        // Supabase's enumeration protection makes signUp() SUCCEED even when the
        // email already has a confirmed account: it returns an obfuscated user
        // with an EMPTY identities array and sends no email. Without this check
        // the user gets parked on /verify-email waiting for an email that will
        // never arrive. Detect it and steer them to log in / reset instead.
        const alreadyRegistered =
          Array.isArray(data?.user?.identities) && data.user.identities.length === 0;
        if (alreadyRegistered) {
          setIsSignUp(false);
          setPassword('');
          setShowExistingModal(true);
          setLoading(false);
          return;
        }

        // If session is null, Supabase requires email confirmation
        if (!data.session) {
          navigate('/verify-email', { state: { email, emailJustSent: true } });
        }
        // Otherwise AuthContext will pick up the session and the useEffect above navigates
      } else {
        const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
        if (signInError) throw signInError;
        // Navigation is handled by the useEffect above once AuthContext sets user —
        // avoids a race where we navigate before user is set in context
      }
    } catch (err) {
      // Make Supabase error messages friendlier
      const msg = err.message || '';
      if (msg.includes('Email not confirmed')) {
        // Account exists but was never verified. Don't strand the user on an
        // error they can't act on (previously this required deleting the account
        // in Supabase) — send them to the verify screen to get a fresh code.
        setLoading(false);
        navigate('/verify-email', { state: { email, emailJustSent: false } });
        return;
      } else if (msg.includes('Invalid login credentials')) {
        setError('Incorrect email or password.');
      } else {
        setError(msg);
      }
      setLoading(false);
    }
  };

  // Native Apple / Google sign-in. On success Supabase sets a session and the
  // routing effect above takes over (new users land on /role-select onboarding
  // because their profile row doesn't exist yet).
  const handleSocial = async (provider) => {
    setError('');
    setNotice('');
    setSocialLoading(provider);
    try {
      if (provider === 'apple') await signInWithApple();
      else await signInWithGoogle();
      // Navigation is handled by the auth-state effect once the session lands.
    } catch (err) {
      if (err?.message !== SOCIAL_CANCELLED) {
        setError(err?.message || `Could not sign in with ${provider === 'apple' ? 'Apple' : 'Google'}.`);
      }
    } finally {
      setSocialLoading('');
    }
  };

  // Opens the focused email step (email + password). Apple/Google are PROVIDER
  // choices — each handles new-and-returning users on its own — so they don't
  // belong on this email-only step; they live on the landing hub, one
  // "‹ All sign-in options" tap away. The step defaults to log in and keeps a
  // toggle to switch to sign up.
  const openEmail = (signUp) => {
    setEmailMode(true);
    setIsSignUp(signUp);
    setError('');
    setNotice('');
  };

  // Don't render the login form at all for an authenticated user
  if (!authLoading && user) return null;

  const statusMessages = (
    <>
      {error && <div style={styles.error} className="n-enter">{error}</div>}
      {notice && <div style={styles.notice} className="n-enter">{notice}</div>}
    </>
  );

  const footer = (
    <div style={styles.footer}>
      <p style={styles.legal}>
        By continuing you agree to our{' '}
        <a href="https://nomadmktplace.com/terms.html" target="_blank" rel="noopener noreferrer" style={styles.legalLink}>Terms</a>{' '}and{' '}
        <a href="https://nomadmktplace.com/privacy.html" target="_blank" rel="noopener noreferrer" style={styles.legalLink}>Privacy Policy</a>.
      </p>
      {/* Guest browsing — App Store Guideline 5.1.1(v): browsing listings must
          not require an account. Saving, liking, chat etc. still prompt sign-up. */}
      <button style={styles.guestLink} className="n-press" onClick={() => navigate('/swipe')}>
        Browse listings without an account ›
      </button>
      <button style={styles.aboutLink} className="n-press" onClick={() => navigate('/about')}>
        About Nomad
      </button>
    </div>
  );

  return (
    <div style={styles.container} className="nomad-auth">
      <style>{SCREEN_CSS}</style>

      {/* Decorative background glows */}
      <div style={styles.glowTopLeft} aria-hidden="true" />
      <div style={styles.glowRight} aria-hidden="true" />

      <div style={{ ...styles.scroll, justifyContent: showChooser ? 'flex-start' : 'center' }}>
        <div style={styles.column}>
          {showChooser ? (
            <>
              {/* 1. Decorative fanned card stack */}
              <div style={styles.stack} className="n-enter-cards">
                <div style={{ ...styles.cardBack, ...styles.cardBackLeft }}>
                  <div style={{ ...styles.cardPhoto, backgroundImage: `url(${STACK_LISTINGS[0].photo})` }} />
                  <div style={{ ...styles.cardOverlay, background: 'rgba(11,27,51,.62)' }} />
                </div>
                <div style={{ ...styles.cardBack, ...styles.cardBackRight }}>
                  <div style={{ ...styles.cardPhoto, backgroundImage: `url(${STACK_LISTINGS[1].photo})` }} />
                  <div style={{ ...styles.cardOverlay, background: 'rgba(11,27,51,.5)' }} />
                </div>
                <div style={styles.cardFront}>
                  <div style={{ ...styles.cardPhoto, backgroundImage: `url(${STACK_LISTINGS[2].photo})` }} />
                  <div style={styles.cardFrontWash} />
                  <div style={styles.cardFrontScrim} />
                  <div style={styles.saveStamp}>SAVE</div>
                  <div style={styles.caption}>
                    <span style={styles.captionHood}>{STACK_LISTINGS[2].neighborhood}</span>
                    <span style={styles.captionFig}>{STACK_LISTINGS[2].figures}</span>
                  </div>
                </div>
              </div>

              {/* 2. Wordmark */}
              <div style={styles.wordmarkBlock} className="n-enter n-enter-wordmark">
                <h1 style={styles.logo}>nomad</h1>
                <p style={styles.slogan}>Swipe. Match. Move In.</p>
              </div>

              {/* 3. Button stack */}
              <div style={styles.buttonStack} className="n-enter n-enter-buttons">
                <button style={{ ...styles.btn, ...styles.emailBtn }} className="n-press" onClick={() => openEmail(false)}>
                  Continue with email
                </button>
                <button
                  style={{ ...styles.btn, ...styles.glassBtn }}
                  className="n-press"
                  onClick={() => handleSocial('apple')}
                  disabled={!!socialLoading}
                >
                  {socialLoading === 'apple' ? 'Signing in…' : (<><AppleGlyph /> Continue with Apple</>)}
                </button>
                <button
                  style={{ ...styles.btn, ...styles.glassBtn }}
                  className="n-press"
                  onClick={() => handleSocial('google')}
                  disabled={!!socialLoading}
                >
                  {socialLoading === 'google' ? 'Signing in…' : (<><GoogleGlyph /> Continue with Google</>)}
                </button>

                {statusMessages}
              </div>

              {footer}
            </>
          ) : (
            <>
              {/* Email step — same navy ground. Reached from "Continue with email"
                  / "Log in" (social builds) or shown directly (no-social web). */}
              {showSocial && (
                <button
                  style={styles.backLink}
                  className="n-press"
                  onClick={() => { setEmailMode(false); setError(''); setNotice(''); }}
                >
                  ‹ All sign-in options
                </button>
              )}

              <div style={{ ...styles.wordmarkBlock, ...(formIsPrimary ? styles.wordmarkBlockPrimary : styles.wordmarkBlockCompact) }} className="n-enter">
                <h1 style={formIsPrimary ? styles.logo : styles.logoCompact}>nomad</h1>
                <p style={styles.slogan}>Swipe. Match. Move In.</p>
              </div>

              <div style={styles.form} className="n-enter n-enter-wordmark">
                <input
                  style={styles.input}
                  className="n-input"
                  type="email"
                  placeholder="Email"
                  autoComplete="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                />
                <input
                  style={styles.input}
                  className="n-input"
                  type="password"
                  placeholder="Password"
                  autoComplete={isSignUp ? 'new-password' : 'current-password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                />
                {isSignUp && (
                  <input
                    style={styles.input}
                    className="n-input"
                    type="password"
                    placeholder="Confirm password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                  />
                )}

                <button style={{ ...styles.btn, ...styles.emailBtn, marginTop: '3px' }} className="n-press" onClick={handleSubmit} disabled={loading}>
                  {loading ? 'Please wait…' : isSignUp ? 'Create account' : 'Log in'}
                </button>

                {!isSignUp && (
                  <p style={styles.forgot}>
                    <span style={styles.forgotLink} className="n-press" onClick={loading ? undefined : handleForgotPassword}>
                      Forgot password?
                    </span>
                  </p>
                )}

                {statusMessages}

                <p style={styles.loginRow}>
                  {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
                  <span
                    style={styles.loginLink}
                    className="n-press"
                    onClick={() => { setIsSignUp(!isSignUp); setError(''); setNotice(''); setConfirmPassword(''); }}
                  >
                    {isSignUp ? 'Log in' : 'Sign up'}
                  </span>
                </p>
              </div>

              {footer}
            </>
          )}
        </div>
      </div>

      {showExistingModal && (
        <div style={styles.modalOverlay} onClick={() => setShowExistingModal(false)}>
          <div style={styles.modalCard} onClick={e => e.stopPropagation()}>
            <button
              style={styles.modalClose}
              className="n-press"
              onClick={() => setShowExistingModal(false)}
              aria-label="Dismiss"
            >
              ×
            </button>
            <h3 style={styles.modalTitle}>You already have an account</h3>
            <p style={styles.modalBody}>
              An account with <strong style={{ color: '#fff' }}>{email}</strong> already exists. Log in below — or
              tap "Forgot password?" if you don't remember it.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Design 5a tokens ────────────────────────────────────────────────────────
// This is a fixed, branded screen: it uses the app's navy palette directly
// (not the light/dark theme variables) so it looks identical in both themes,
// the way a splash/auth screen should. Values are from the handoff.
const INK = '#0B1B33';
const CARD_FALLBACK = '#16294A';
const MINT = '#8FC4BC';
const MINT_TEXT = '#B7DDD7';
const SECONDARY = '#9FB4D6';
const GRADIENT = 'linear-gradient(120deg, #4E8E8E, #2A4A9B)';
const QUICKSAND = "'Quicksand', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', sans-serif";

const styles = {
  container: {
    position: 'relative',
    minHeight: '100%', height: '100%',
    background: INK,
    overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
    fontFamily: QUICKSAND,
    boxSizing: 'border-box',
  },

  // Background glows — purely decorative
  glowTopLeft: {
    position: 'absolute', top: '-110px', left: '-70px',
    width: '360px', height: '360px', pointerEvents: 'none',
    background: 'radial-gradient(circle, rgba(78,142,142,.34), rgba(78,142,142,0) 70%)',
  },
  glowRight: {
    position: 'absolute', top: '150px', right: '-120px',
    width: '340px', height: '340px', pointerEvents: 'none',
    background: 'radial-gradient(circle, rgba(42,74,155,.38), rgba(42,74,155,0) 70%)',
  },

  // Scroll safety wrapper (screen is designed not to scroll at 874px; this only
  // engages on very short devices so the buttons/footer are never cut off).
  scroll: {
    position: 'relative', zIndex: 1,
    flex: 1, minHeight: 0,
    overflowY: 'auto', WebkitOverflowScrolling: 'touch',
    display: 'flex', flexDirection: 'column',
  },
  column: {
    width: '100%', maxWidth: '440px', margin: '0 auto',
    padding: 'calc(env(safe-area-inset-top, 0px) + 8px) 26px calc(env(safe-area-inset-bottom, 0px) + 40px)',
    boxSizing: 'border-box',
    display: 'flex', flexDirection: 'column',
  },

  // 1. Card stack ------------------------------------------------------------
  stack: { position: 'relative', height: '312px', margin: '0 -26px', flex: 'none' },
  cardBack: {
    position: 'absolute', left: '50%', width: '198px', height: '236px', marginLeft: '-99px',
    borderRadius: '22px', background: CARD_FALLBACK, border: '1px solid rgba(255,255,255,.09)',
    boxShadow: '0 20px 44px rgba(0,0,0,.4)', overflow: 'hidden',
  },
  cardBackLeft: { top: '82px', transform: 'rotate(-9deg) translateX(-30px)' },
  cardBackRight: { top: '77px', transform: 'rotate(7deg) translateX(33px)' },
  cardFront: {
    position: 'absolute', left: '50%', top: '63px', width: '210px', height: '251px', marginLeft: '-105px',
    borderRadius: '24px', background: CARD_FALLBACK, border: '1px solid rgba(255,255,255,.12)',
    boxShadow: '0 30px 66px rgba(0,0,0,.55)', overflow: 'hidden', transform: 'rotate(-2.5deg)',
  },
  cardPhoto: { position: 'absolute', inset: 0, backgroundSize: 'cover', backgroundPosition: 'center' },
  cardOverlay: { position: 'absolute', inset: 0 },
  cardFrontWash: {
    position: 'absolute', inset: 0,
    background: 'linear-gradient(120deg, rgba(78,142,142,.34), rgba(42,74,155,.14) 60%, rgba(11,27,51,.1))',
  },
  cardFrontScrim: {
    position: 'absolute', inset: 0,
    background: 'linear-gradient(to bottom, rgba(11,27,51,.35) 0%, rgba(11,27,51,0) 40%, rgba(11,27,51,.88) 100%)',
  },
  saveStamp: {
    position: 'absolute', top: '15px', left: '13px', padding: '6px 11px',
    border: `2.5px solid ${MINT}`, borderRadius: '9px', transform: 'rotate(-11deg)',
    background: 'rgba(11,27,51,.3)', color: MINT, fontSize: '17px', fontWeight: 700,
    letterSpacing: '.06em', lineHeight: 1,
  },
  caption: { position: 'absolute', left: 0, right: 0, bottom: 0, padding: '0 14px 13px', display: 'flex', flexDirection: 'column', gap: '3px' },
  captionHood: { fontSize: '11px', fontWeight: 600, color: MINT, letterSpacing: '.04em' },
  captionFig: { fontSize: '14.5px', fontWeight: 600, color: '#fff', lineHeight: 1.2 },

  // 2. Wordmark --------------------------------------------------------------
  wordmarkBlock: { flex: 'none', padding: '68px 0 30px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' },
  wordmarkBlockCompact: { padding: '10px 0 26px' },
  wordmarkBlockPrimary: { padding: 'calc(env(safe-area-inset-top, 0px) + 40px) 0 30px' },
  logo: { margin: 0, fontSize: '44px', fontWeight: 500, color: '#fff', letterSpacing: '.01em', lineHeight: 1, fontFamily: QUICKSAND },
  logoCompact: { margin: 0, fontSize: '36px', fontWeight: 500, color: '#fff', letterSpacing: '.01em', lineHeight: 1, fontFamily: QUICKSAND },
  slogan: { margin: 0, fontSize: '17px', fontWeight: 500, color: MINT_TEXT, letterSpacing: '.02em' },

  // 3. Buttons ---------------------------------------------------------------
  buttonStack: { flex: 'none', display: 'flex', flexDirection: 'column', gap: '11px' },
  btn: {
    width: '100%', height: '58px', borderRadius: '999px', border: 'none',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '9px',
    fontSize: '17px', fontWeight: 600, color: '#fff', cursor: 'pointer',
    fontFamily: QUICKSAND, boxSizing: 'border-box', padding: 0,
  },
  emailBtn: { background: GRADIENT, boxShadow: '0 14px 32px rgba(42,74,155,.5)' },
  glassBtn: { background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.22)' },

  loginRow: { paddingTop: '12px', textAlign: 'center', fontSize: '15px', fontWeight: 500, color: SECONDARY, margin: 0 },
  loginLink: { fontSize: '15px', fontWeight: 700, color: MINT, borderBottom: `1px solid rgba(143,196,188,.5)`, paddingBottom: '1px', cursor: 'pointer', display: 'inline-block' },

  // Email form ---------------------------------------------------------------
  form: { flex: 'none', display: 'flex', flexDirection: 'column', gap: '11px' },
  backLink: { flex: 'none', alignSelf: 'flex-start', background: 'none', border: 'none', padding: '2px 0', color: SECONDARY, fontSize: '15px', fontWeight: 600, cursor: 'pointer', fontFamily: QUICKSAND },
  input: {
    width: '100%', boxSizing: 'border-box', padding: '16px 18px', borderRadius: '14px',
    background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.16)',
    color: '#fff', fontSize: '16px', outline: 'none', fontFamily: QUICKSAND,
  },
  forgot: { textAlign: 'center', margin: '-2px 0 0' },
  forgotLink: { color: SECONDARY, fontSize: '13.5px', fontWeight: 500, cursor: 'pointer' },

  // Status ------------------------------------------------------------------
  error: { color: '#ffb4ab', background: 'rgba(231,76,60,.15)', border: '1px solid rgba(231,76,60,.4)', padding: '10px 14px', borderRadius: '12px', fontSize: '13.5px', textAlign: 'center', margin: 0, lineHeight: 1.45 },
  notice: { color: MINT, background: 'rgba(78,142,142,.18)', border: '1px solid rgba(143,196,188,.35)', padding: '10px 14px', borderRadius: '12px', fontSize: '13.5px', textAlign: 'center', margin: 0, lineHeight: 1.5 },

  // Footer ------------------------------------------------------------------
  footer: { flex: 'none', paddingTop: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '11px' },
  legal: { margin: 0, fontSize: '12.5px', fontWeight: 500, lineHeight: 1.5, color: 'rgba(159,180,214,.75)', textAlign: 'center' },
  legalLink: { color: MINT_TEXT, borderBottom: '1px solid rgba(183,221,215,.45)', textDecoration: 'none' },
  guestLink: { background: 'none', border: 'none', cursor: 'pointer', fontFamily: QUICKSAND, fontSize: '14.5px', fontWeight: 700, color: 'rgba(214,226,245,.92)', padding: '6px 8px' },
  aboutLink: { background: 'none', border: 'none', cursor: 'pointer', fontFamily: QUICKSAND, fontSize: '12.5px', fontWeight: 600, color: 'rgba(159,180,214,.6)', textTransform: 'uppercase', letterSpacing: '.1em', padding: '2px 4px' },

  // "You already have an account" modal -------------------------------------
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', zIndex: 1000 },
  modalCard: { position: 'relative', background: CARD_FALLBACK, border: '1px solid rgba(255,255,255,.1)', borderRadius: '20px', padding: '30px 24px 24px', width: '100%', maxWidth: '340px', boxShadow: '0 30px 66px rgba(0,0,0,.55)', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '10px', fontFamily: QUICKSAND },
  modalClose: { position: 'absolute', top: '8px', right: '12px', background: 'none', border: 'none', fontSize: '26px', lineHeight: 1, color: SECONDARY, cursor: 'pointer', padding: '4px', fontFamily: QUICKSAND },
  modalTitle: { margin: '2px 0 0', fontSize: '18px', fontWeight: 700, color: '#fff' },
  modalBody: { margin: 0, fontSize: '14px', color: '#DCE7F7', lineHeight: 1.55 },
};
